import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { DynamicFormV2 } from "@shared/components/DynamicFormV2";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { StepHelpers } from "@shared/components/Stepper";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { AlertCircle, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCategoryOptions,
  useCreateConsumer,
  useEditConsumer,
  useSubcategoryOptions,
  useUpdateConsumer,
} from "../../../hooks";
import { consumerApi } from "../../../api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";

interface ConsumerInformationProps {
  storageKey: string;
  logUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

interface ConsumerData {
  // Primary Consumer
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  category: string;
  categoryDisplay?: string;
  subCategory: string;
  subCategoryDisplay?: string;
  isVip: boolean;
  vipLevel?: string;

  // Secondary Consumer
  hasSecondary: boolean;
  secondaryFirstName?: string;
  secondaryLastName?: string;
  secondaryPhoneNumber?: string;
  secondaryEmail?: string;
  secondaryRelationship?: string;

  // Metadata
  emailVerified?: boolean;
  secondaryEmailVerified?: boolean;
  timestamp?: string;
}

export function ConsumerDetailsStep({
  storageKey,
  logUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  setValidationError,
  clearValidationError,
  isValidating,
  ...otherProps
}: ConsumerInformationProps) {
  console.log("🔍 ConsumerDetailsStep props:", {
    storageKey,
    hasStepHelpers: !!stepHelpers,
    currentStepIndex,
    hasOnNext: !!onNext,
    hasSetValidationError: !!setValidationError,
    hasClearValidationError: !!clearValidationError,
    isValidating,
    otherProps: Object.keys(otherProps),
  });

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const previousCategoryRef = useRef<string>("");
  const createConsumerMutation = useCreateConsumer();
  const editConsumerMutation = useEditConsumer();
  const { consumerId } = useParams<{ consumerId: string }>();

  // Get remote utility ID
  const remoteUtilityId = useRemoteUtilityId();

  // Fetch categories using hooks
  const { data: categories, isLoading: isLoadingCategories } =
    useCategoryOptions(Number(remoteUtilityId));

  // Transform categories to match form field format
  const consumerCategories = useMemo(() => {
    return categories?.result?.map((category: any) => ({
      value: category.code,
      label: category.name,
    }));
  }, [categories]);

  // Helper function to save step data using StepHelpers
  const saveStepData = useCallback(
    (data: Partial<ConsumerData>) => {
      console.log("💾 saveStepData called with:", data);
      console.log("💾 stepHelpers available:", !!stepHelpers);

      if (!stepHelpers) {
        console.log("❌ No stepHelpers available, cannot save data");
        return;
      }

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        // Use StepHelpers to save step data
        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log(
          "Consumer information step data saved via StepHelpers:",
          stepData
        );

        // Also save to localStorage as backup
        try {
          const existingData = localStorage.getItem(storageKey);
          let allStepData = existingData ? JSON.parse(existingData) : {};

          // Ensure we have the correct structure
          if (!allStepData.stepData) {
            allStepData = {
              currentStep: currentStepIndex,
              completedSteps: [],
              skippedSteps: [],
              stepData: {},
              timestamp: Date.now(),
              version: "1.0.0",
            };
          }

          allStepData.stepData[currentStepIndex] = stepData;
          allStepData.timestamp = Date.now();
          localStorage.setItem(storageKey, JSON.stringify(allStepData));
          console.log("Step data also saved to localStorage as backup");
        } catch (error) {
          console.error("Failed to save to localStorage backup:", error);
        }
      } catch (error) {
        console.error(
          "Failed to save consumer information step data via StepHelpers:",
          error
        );
      }
    },
    [stepHelpers, currentStepIndex, storageKey]
  );

  // State for email verification
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<{
    primary: "unverified" | "verifying" | "verified" | "failed";
    secondary: "unverified" | "verifying" | "verified" | "failed";
  }>({
    primary: "unverified",
    secondary: "unverified",
  });

  // Email verification for form submission (without UI updates)
  const verifyEmailForSubmission = useCallback(
    async (
      email: string,
      emailType: "primary" | "secondary"
    ): Promise<boolean> => {
      console.log(`🚀 Verifying ${emailType} email for submission: ${email}`);

      if (!email || email.length < 5 || !email.includes("@")) {
        return false;
      }

      try {
        // Set verification status to verifying
        setEmailVerificationStatus((prev) => ({
          ...prev,
          [emailType]: "verifying",
        }));

        // Direct API call
        const response = await consumerApi.getEmailVerify(
          email,
          Number(remoteUtilityId)
        );
        console.log("📡 API Response for submission:", response);

        // Check if verification was successful
        const isVerified =
          response &&
          (response.success === true ||
            response.verified === true ||
            response.status === "success" ||
            (response.message &&
              response.message.toLowerCase().includes("success")) ||
            (response.message &&
              response.message.toLowerCase().includes("verified")));

        if (isVerified) {
          // Success
          setEmailVerificationStatus((prev) => ({
            ...prev,
            [emailType]: "verified",
          }));

          // Update form data
          const formData = consumerForm.getValues();
          const verificationField =
            emailType === "primary"
              ? "emailVerified"
              : "secondaryEmailVerified";
          const updatedData = {
            ...formData,
            [verificationField]: true,
          };

          // Save updated data
          saveStepData(updatedData);

          console.log(`✅ ${emailType} email verified successfully`);
          return true;
        } else {
          // Failed verification
          setEmailVerificationStatus((prev) => ({
            ...prev,
            [emailType]: "failed",
          }));

          console.log(`❌ ${emailType} email verification failed`);
          return false;
        }
      } catch (error: any) {
        console.error(`❌ ${emailType} email verification error:`, error);

        // Set verification status to failed
        setEmailVerificationStatus((prev) => ({
          ...prev,
          [emailType]: "failed",
        }));

        return false;
      }
    },
    [remoteUtilityId, saveStepData]
  );

  // Create stable base fields
  const baseConsumerFields: FormField[] = useMemo(
    () => [
      {
        name: "firstName",
        label: "First Name",
        type: "text",
        required: true,
        placeholder: "Enter first name",
        helperText:
          "Enter the legal first name as it appears on identification documents",
        showHelperTooltip: true,
        tooltipText: "This should match government-issued identification",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 1,
      },
      {
        name: "lastName",
        label: "Last Name",
        type: "text",
        required: true,
        placeholder: "Enter last name",
        helperText:
          "Enter the legal last name as it appears on identification documents",
        showHelperTooltip: true,
        tooltipText: "This should match government-issued identification",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 2,
      },
      {
        name: "phoneNumber",
        label: "Phone Number",
        type: "phone",
        required: true,
        placeholder: "Enter phone number",
        helperText:
          "This number will be used for account verification and service notifications",
        showHelperTooltip: true,
        tooltipText:
          "We'll send verification codes to this number for security",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 3,
      },
      // Primary Email Field
      {
        name: "email",
        label: "Email",
        type: "email",
        required: true,
        placeholder: "Enter email address",
        helperText:
          "This email will receive account information, bills, and service updates",
        showHelperTooltip: true,
        tooltipText:
          "Make sure this email is accessible as it will be used for important notifications",
        // disabled: emailVerificationStatus.primary === 'verified',
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 4,
      },
      {
        name: "category",
        label: "Category",
        type: "select",
        required: true,
        placeholder: "Select category",
        options: [], // Will be populated dynamically
        helperText:
          "The category affects available service plans, billing rates, and service prioritization",
        showHelperTooltip: true,
        tooltipText: "This determines your service tier and available options",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // select:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 5,
      },
      {
        name: "subCategory",
        label: "Sub-Category",
        type: "select",
        required: true,
        placeholder: "Select sub-category",
        options: [], // Will be populated dynamically
        helperText:
          "Further classification determines specific service options and billing structures",
        showHelperTooltip: true,
        tooltipText:
          "This provides more specific service customization options",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // select:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 6,
      },
      // VIP Designation
      {
        name: "isVip",
        label: "VIP Designation",
        type: "checkbox",
        required: false,
        helperText: "Mark this consumer as a VIP for priority handling",
        showHelperTooltip: true,
        tooltipText:
          "VIP customers receive priority service, dedicated support, and special rates",
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 7,
      },
      {
        name: "hasSecondary",
        label: "Add Secondary Consumer",
        type: "checkbox",
        required: false,
        helperText:
          "Add another person who shares responsibility for this account",
        showHelperTooltip: true,
        tooltipText:
          "Secondary consumers can make account changes and receive notifications",
        group: "Primary Consumer Information",
        groupColor: "border-l-blue-500",
        groupOrder: 8,
      },
      // Secondary Consumer
      {
        name: "secondaryRelationship",
        label: "Relationship to Primary",
        type: "select",
        required: false,
        placeholder: "Select relationship",
        options: [
          { value: "spouse", label: "Spouse" },
          { value: "relative", label: "Relative" },
          { value: "business_partner", label: "Business Partner" },
          { value: "other", label: "Other" },
        ],
        helperText:
          "This defines the relationship between the primary and secondary account holders",
        showWhen: (formValues) => formValues.hasSecondary === true,
        showHelperTooltip: true,
        tooltipText: "Required for legal and billing purposes",
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // select:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Secondary Consumer",
        groupColor: "border-l-green-500",
        groupOrder: 2,
      },
      {
        name: "secondaryFirstName",
        label: "Secondary First Name",
        type: "text",
        required: false,
        placeholder: "Enter first name",
        helperText:
          "Enter the legal first name of the secondary account holder",
        showWhen: (formValues) => formValues.hasSecondary === true,
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Secondary Consumer",
        groupColor: "border-l-green-500",
        groupOrder: 3,
      },
      {
        name: "secondaryLastName",
        label: "Secondary Last Name",
        type: "text",
        required: false,
        placeholder: "Enter last name",
        helperText: "Enter the legal last name of the secondary account holder",
        showWhen: (formValues) => formValues.hasSecondary === true,
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Secondary Consumer",
        groupColor: "border-l-green-500",
        groupOrder: 4,
      },
      {
        name: "secondaryPhoneNumber",
        label: "Secondary Phone Number",
        type: "phone",
        required: false,
        placeholder: "Enter phone number",
        helperText: "Phone number for the secondary account holder",
        showWhen: (formValues) => formValues.hasSecondary === true,
        classes: {
          container: "mb-4",
          // label: "block text-sm font-medium text-gray-700 mb-2",
          // input:
          //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          // error: "text-red-600 text-sm mt-1",
          // helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Secondary Consumer",
        groupColor: "border-l-green-500",
        groupOrder: 5,
      },
      // Secondary Email Field
      {
        name: "secondaryEmail",
        label: "Secondary Email",
        type: "email",
        required: false,
        placeholder: "Enter email address",
        helperText: "Email address for the secondary account holder",
        showHelperTooltip: true,
        tooltipText: "Secondary email for account notifications",
        // disabled: emailVerificationStatus.secondary === 'verified',
        showWhen: (formValues) => formValues.hasSecondary === true,
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Secondary Consumer",
        groupColor: "border-l-green-500",
        groupOrder: 6,
      },
    ],
    [emailVerificationStatus]
  );

  // Create form instance
  const consumerForm = FormService.createForm<ConsumerData>({
    fields: baseConsumerFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      category: "",
      categoryDisplay: "",
      subCategory: "",
      subCategoryDisplay: "",
      isVip: false,
      vipLevel: "",
      hasSecondary: false,
      secondaryFirstName: "",
      secondaryLastName: "",
      secondaryPhoneNumber: "",
      secondaryEmail: "",
      secondaryRelationship: "",
      emailVerified: false,
      secondaryEmailVerified: false,
    },
  });

  // Watch form values for dynamic updates
  const watchCategory = consumerForm.watch("category");
  const watchIsVip = consumerForm.watch("isVip");

  // Fetch subcategories using hooks
  const { data: subcategories, isLoading: isLoadingSubcategories } =
    useSubcategoryOptions(Number(remoteUtilityId), watchCategory || "");

  // Transform subcategories to match form field format and update available subcategories
  const transformedSubcategories = useMemo(() => {
    if (!watchCategory || !subcategories?.result?.length) return [];

    return subcategories?.result?.map((subcategory: any) => ({
      value: subcategory.code,
      label: subcategory.name,
    }));
  }, [watchCategory, subcategories]);

  const dynamicFields = useMemo(() => {
    console.log("Building dynamic fields with categories:", consumerCategories);

    return baseConsumerFields.map((field) => {
      if (field.name === "subCategory") {
        return {
          ...field,
          options: transformedSubcategories || [],
          placeholder:
            watchCategory && isLoadingSubcategories
              ? "Loading sub-categories..."
              : watchCategory
              ? "Select sub-category"
              : "Select category first",
        };
      }
      if (field.name === "category") {
        const categoryField = {
          ...field,
          options: consumerCategories || [],
          placeholder: isLoadingCategories
            ? "Loading categories..."
            : "Select category",
        };
        console.log("Category field built:", categoryField);
        return categoryField;
      }
      return field;
    });
  }, [
    baseConsumerFields,
    transformedSubcategories,
    consumerCategories,
    watchCategory,
    isLoadingSubcategories,
    isLoadingCategories,
  ]);

  // Load saved verification status on mount
  useEffect(() => {
    if (isDataLoaded) {
      const formData = consumerForm.getValues();
      if (formData.emailVerified) {
        setEmailVerificationStatus((prev) => ({
          ...prev,
          primary: "verified",
        }));
      }
      if (formData.secondaryEmailVerified) {
        setEmailVerificationStatus((prev) => ({
          ...prev,
          secondary: "verified",
        }));
      }
    }
  }, [isDataLoaded, consumerForm]);

  // Watch email values for verification buttons
  const watchEmail = consumerForm.watch("email");
  const watchSecondaryEmail = consumerForm.watch("secondaryEmail");
  const watchHasSecondary = consumerForm.watch("hasSecondary");

useEffect(() => {
  console.log("🔍 ConsumerDetailsStep: Loading data for step:", currentStepIndex);
  console.log("🔍 StepHelpers available:", !!stepHelpers);
  
  const loadData = () => {
    let savedStepData = null;
    
    // 🎯 FIRST: Try fallback consumerFirstStepData
    try {
      console.log("📦 Trying fallback: consumerFirstStepData");
      const fallbackPayload = JSON.parse(
        localStorage.getItem("consumerFirstStepData") || "{}"
      );
      console.log("📦 Fallback payload:", fallbackPayload);
      
      const fallbackPrimary = fallbackPayload?.consumer_details?.primaryConsumer || {};
      const fallbackSecondary = fallbackPayload?.consumer_details?.secondaryConsumer || {};
      
      // Use fallback if we have some primary consumer data
      if (Object.keys(fallbackPrimary).length > 0) {
        savedStepData = {
          firstName: fallbackPrimary.firstName || "",
          lastName: fallbackPrimary.lastName || "",
          phoneNumber: fallbackPrimary.contactNumber || "",
          email: fallbackPrimary.email || "",
          category: fallbackPrimary.category || "",
          categoryDisplay: fallbackPrimary.categoryDisplay || "",
          subCategory: fallbackPrimary.subCategory || "",
          subCategoryDisplay: fallbackPrimary.subCategoryDisplay || "",
          isVip: fallbackPrimary.isVip || false,
          vipLevel: "",
          hasSecondary: fallbackSecondary && Object.keys(fallbackSecondary).length > 0,
          secondaryFirstName: fallbackSecondary.firstName || "",
          secondaryLastName: fallbackSecondary.lastName || "",
          secondaryPhoneNumber: fallbackSecondary.contactNumber || "",
          secondaryEmail: fallbackSecondary.email || "",
          secondaryRelationship: fallbackSecondary.relationship || "",
          emailVerified: false,
          secondaryEmailVerified: false,
        };
        console.log("📦 Created fallback data structure:", savedStepData);
      }
    } catch (error) {
      console.error("❌ Fallback localStorage error:", error);
    }
    
    // 🎯 SECOND: Try StepHelpers if fallback didn't provide data
    if (!savedStepData || Object.keys(savedStepData).length === 0) {
      if (stepHelpers) {
        savedStepData = stepHelpers.getStepData<ConsumerData>(currentStepIndex);
        console.log("📊 StepHelpers data:", savedStepData);
      }
    }
    
    // 🎯 THIRD: Try original localStorage format if still no data
    if (!savedStepData || Object.keys(savedStepData).length === 0) {
      try {
        console.log("💾 Trying localStorage with key:", storageKey);
        const localStorageData = localStorage.getItem(storageKey);
        if (localStorageData) {
          const parsed = JSON.parse(localStorageData);
          // Check if it's the new format with stepData object
          if (parsed.stepData && parsed.stepData[currentStepIndex.toString()]) {
            savedStepData = parsed.stepData[currentStepIndex.toString()];
            console.log("💾 Found step data in localStorage (new format):", savedStepData);
          } else if (parsed[currentStepIndex.toString()]) {
            // Fallback to old format
            savedStepData = parsed[currentStepIndex.toString()];
            console.log("💾 Found step data in localStorage (old format):", savedStepData);
          }
        }
      } catch (error) {
        console.error("❌ localStorage error:", error);
      }
    }
    
    // 🎯 POPULATE FORM: If we have data from any source
    if (savedStepData && Object.keys(savedStepData).length > 0) {
      const dataSource = localStorage.getItem("consumerFirstStepData") && 
                        JSON.parse(localStorage.getItem("consumerFirstStepData") || "{}")?.consumer_details?.primaryConsumer ? 
                        "fallback" :
                        stepHelpers && Object.keys(stepHelpers.getStepData<ConsumerData>(currentStepIndex) || {}).length > 0 ? 
                        "StepHelpers" : "localStorage";
      
      console.log("✅ Populating form with data from:", dataSource);
      
      consumerForm.reset(savedStepData);
      
      // Handle category reference for subcategory loading
      if (savedStepData.category) {
        previousCategoryRef.current = savedStepData.category;
      }
      
      // Restore email verification states
      if (savedStepData.emailVerified) {
        setEmailVerificationStatus((prev) => ({
          ...prev,
          primary: "verified",
        }));
      }
      if (savedStepData.secondaryEmailVerified) {
        setEmailVerificationStatus((prev) => ({
          ...prev,
          secondary: "verified",
        }));
      }
      
      console.log("✅ Form populated successfully with:", savedStepData);
    } else {
      console.log("ℹ️ No saved data found from any source for step:", currentStepIndex);
    }
    
    setIsDataLoaded(true);
  };
  
  loadData();
}, [currentStepIndex, stepHelpers, storageKey, consumerForm]);

  
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      console.log("🔄 handleFieldChange called:", fieldName, value);

      // Get current form data first
      let currentFormData = consumerForm.getValues();

      // If category is changing, reset subcategory
      if (fieldName === "category" && value !== previousCategoryRef.current) {
        currentFormData.subCategory = "";
        currentFormData.subCategoryDisplay = "";
        previousCategoryRef.current = value;
      }

      // Clear secondary fields when hasSecondary is false
      if (fieldName === "hasSecondary" && value === false) {
        currentFormData.secondaryFirstName = "";
        currentFormData.secondaryLastName = "";
        currentFormData.secondaryPhoneNumber = "";
        currentFormData.secondaryEmail = "";
        currentFormData.secondaryRelationship = "";
        currentFormData.secondaryEmailVerified = false;

        // Clear email verification status for secondary
        setEmailVerificationStatus((prev) => ({
          ...prev,
          secondary: "unverified",
        }));
      }

      // Update the changed field
      currentFormData[fieldName] = value;

      // Handle category and subcategory display names
      if (fieldName === "category") {
        const selectedCategory = consumerCategories?.find(
          (cat) => cat.value === value
        );
        if (selectedCategory) {
          currentFormData.categoryDisplay = selectedCategory.label;
          console.log(
            "📝 Category display name saved:",
            selectedCategory.label
          );
        }
      }

      if (fieldName === "subCategory") {
        const selectedSubCategory = transformedSubcategories?.find(
          (subCat) => subCat.value === value
        );
        if (selectedSubCategory) {
          currentFormData.subCategoryDisplay = selectedSubCategory.label;
          console.log(
            "📝 SubCategory display name saved:",
            selectedSubCategory.label
          );
        }
      }

      // Reset the entire form with the updated data to ensure UI sync
      consumerForm.reset(currentFormData);

      console.log(
        "🔄 Calling saveStepData with updated data:",
        currentFormData
      );
      saveStepData(currentFormData);
    },
    [
      consumerForm,
      saveStepData,
      consumerCategories,
      transformedSubcategories,
      setEmailVerificationStatus,
    ]
  );
  // Check if form is valid for proceeding
  const checkFormValidity = useCallback(() => {
    const formData = consumerForm.getValues();
    const errors = consumerForm.formState.errors;

    // Check required fields
    const requiredFields = [
      "firstName",
      "lastName",
      "phoneNumber",
      "email",
      "category",
      "subCategory",
    ];
    const missingFields = requiredFields.filter(
      (field) => !formData[field as keyof ConsumerData]
    );

    if (missingFields.length > 0) {
      return {
        isValid: false,
        error: `Please complete all required fields: ${missingFields.join(
          ", "
        )}`,
      };
    }
    // Check secondary consumer fields if secondary is enabled
    // Note: Secondary fields are optional (required: false), so we don't validate them as required
    // They should only be validated if the user has started filling them out
    if (formData.hasSecondary) {
      // Only validate secondary fields if at least one has been filled
      const secondaryFields = [
        "secondaryRelationship",
        "secondaryFirstName",
        "secondaryLastName",
      ];
      const filledSecondaryFields = secondaryFields.filter(
        (field) => formData[field as keyof ConsumerData]
      );

      // If user has started filling secondary fields, ensure they complete the essential ones
      if (filledSecondaryFields.length > 0) {
        const essentialSecondaryFields = [
          "secondaryFirstName",
          "secondaryLastName",
        ];
        const missingEssentialFields = essentialSecondaryFields.filter(
          (field) => !formData[field as keyof ConsumerData]
        );

        if (missingEssentialFields.length > 0) {
          return {
            isValid: false,
            error: `Please complete the essential secondary consumer fields: ${missingEssentialFields.join(
              ", "
            )}`,
          };
        }
      }
    }

    let filteredErrors = { ...errors };
    if (!formData.hasSecondary) {
      delete filteredErrors.secondaryEmail;
      delete filteredErrors.secondaryPhoneNumber;
    }

    console.log("🔍 Form validation check:", {
      formData,
      errors,
      hasErrors: Object.keys(filteredErrors).length > 0,
    });

    // Check for any form errors
    if (Object.keys(filteredErrors).length > 0) {
      return {
        isValid: false,
        filteredErrors: "Please fix all validation errors before proceeding",
      };
    }

    return { isValid: true, error: null };
  }, [consumerForm]);

  const initialEmailsRef = useRef<{ email: string; secondaryEmail: string }>({
    email: "",
    secondaryEmail: "",
  });
  // ✅ Correctly get initial values from defaultValues
  useEffect(() => {
    const { email, secondaryEmail } = consumerForm.getValues();
    initialEmailsRef.current = { email, secondaryEmail };
    console.log("📥 Initial email values on mount:", initialEmailsRef.current);
  }, []);

  const handleFormSubmit = useCallback(
    async (data: ConsumerData) => {
      const validity = checkFormValidity();

      if (!validity.isValid) {
        setValidationError?.(validity.error!);
        return;
      }

      const { email: initialEmail, secondaryEmail: initialSecondaryEmail } =
        initialEmailsRef.current;

      console.log("🔄 Submission Debug Info:", {
        initialEmail,
        initialSecondaryEmail,
        currentEmailVerificationStatus: emailVerificationStatus,
        consumerId,
        submittedEmail: data.email,
        submittedSecondaryEmail: data.secondaryEmail,
      });

      try {
        // ✅ Primary email verification
        const shouldVerifyPrimaryEmail =
          data.email &&
          emailVerificationStatus.primary !== "verified" &&
          (!consumerId || data.email !== initialEmail); // ✅ Skip in edit if unchanged

        if (shouldVerifyPrimaryEmail) {
          console.log("🔍 Verifying primary email:", data.email);
          const primaryVerified = await verifyEmailForSubmission(
            data.email,
            "primary"
          );
          if (!primaryVerified) {
            const message = "Primary email verification failed.";
            toast({
              title: "Email Verification Failed",
              description: message,
              variant: "destructive",
            });
            setValidationError?.(message);
            return;
          }
        }

        // ✅ Secondary email verification
        const shouldVerifySecondaryEmail =
          data.hasSecondary &&
          data.secondaryEmail &&
          emailVerificationStatus.secondary !== "verified" &&
          (!consumerId || data.secondaryEmail !== initialSecondaryEmail); // ✅ Skip in edit if unchanged

        if (shouldVerifySecondaryEmail) {
          console.log("🔍 Verifying secondary email:", data.secondaryEmail);
          const secondaryVerified = await verifyEmailForSubmission(
            data.secondaryEmail,
            "secondary"
          );
          if (!secondaryVerified) {
            const message = "Secondary email verification failed.";
            toast({
              title: "Email Verification Failed",
              description: message,
              variant: "destructive",
            });
            setValidationError?.(message);
            return;
          }
        }
      } catch (error) {
        const errorMessage =
          (error as any)?.response?.data?.message?.[0] ||
          (error as any)?.message ||
          "Email verification failed. Please try again.";

        toast({
          title: "Email Verification Failed",
          description: errorMessage,
          variant: "destructive",
        });
        setValidationError?.(errorMessage);
        return;
      }

      saveStepData(data);
      clearValidationError?.();

      const payload = {
        consumer_details: {
          primaryConsumer: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            isVip: data.isVip,
            contactNumber: data.phoneNumber,
            category: data.category,
            categoryDisplay: data.categoryDisplay,
            subCategory: data.subCategory,
            subCategoryDisplay: data.subCategoryDisplay,
          },
          secondaryConsumer: data.hasSecondary
            ? {
                firstName: data.secondaryFirstName,
                lastName: data.secondaryLastName,
                email: data.secondaryEmail,
                contactNumber: data.secondaryPhoneNumber,
                secondaryRelationship: data.secondaryRelationship,
              }
            : undefined,
        },
        remote_utility_id: remoteUtilityId.toString(),
        source: "0",
      };
          // ✅ Save to localStorage for last step use
    localStorage.setItem("consumerFirstStepData", JSON.stringify(payload));

      try {
        const response = consumerId
          ? await editConsumerMutation.mutateAsync({
              type: (currentStepIndex + 1).toString(),
              consumerId,
              payload,
            })
          : await createConsumerMutation.mutateAsync(payload);

        if (!response || response?.error || response?.status >= 400) return;

        logUntilStep("Consumer Detail Step");
        onNext?.();
      } catch (error) {
        const operation = consumerId ? "edit" : "create";
        console.error(`❌ Failed to ${operation} consumer`, error);
        console.error("Payload sent:", payload);
      }
    },
    [
      checkFormValidity,
      clearValidationError,
      consumerId,
      createConsumerMutation,
      currentStepIndex,
      editConsumerMutation,
      emailVerificationStatus,
      onNext,
      remoteUtilityId,
      saveStepData,
      setValidationError,
      verifyEmailForSubmission,
      consumerForm, // ✅ Include form in deps
      initialEmailsRef, // ✅ Added in case it's not stable outside
    ]
  );

  // Custom render for the form footer
  const renderCustomFooter = useCallback(() => {
    const isEditMode = !!consumerId;

    return (
      <div className="flex items-center justify-end pt-6 border-t border-gray-200">
        <Button
          onClick={() => handleFormSubmit(consumerForm.getValues())}
          className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 transition-colors"
        >
          <span>
            {isEditMode ? "Update and Continue" : "Save and Continue"}
          </span>
          <ChevronRight size={16} />
        </Button>
      </div>
    );
  }, [consumerForm, consumerId]);

  return (
    <div className="space-y-6">
      {/* Information Alert Accordion */}
      <Accordion type="single" collapsible className="mb-6">
        <AccordionItem
          value="important-info"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
              <span className="font-medium text-blue-800">
                Important Information
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>All fields marked with an asterisk (*) are required</li>
              <li>The primary consumer will be the main account holder</li>
              <li>Email verification is required before proceeding</li>
              <li>VIP designation requires approval from management</li>
              <li>You can save your progress as a draft at any time</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Dynamic Form */}
      <DynamicFormV2
        key={`consumer-form-${isDataLoaded ? "loaded" : "initial"}`}
        fields={dynamicFields}
        form={consumerForm}
        onSubmit={() => {}}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
        gridColumns={3}
        responsiveColumns={{ sm: 1, md: 3 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
}
